import type { StopOnlineChunkUploadResult } from '@/tauri/online-chunk-upload/session';

export type RustChunkUploadPauseFinalizationOutcome = 'healthy' | 'unhealthy';

type PauseFinalizationEntry = {
  promise: Promise<RustChunkUploadPauseFinalizationOutcome>;
  resolve: (outcome: RustChunkUploadPauseFinalizationOutcome) => void;
};

/**
 * Unconsumed hand-offs. Kept after the promise settles so a later End-session
 * click can still read the pause outcome (pause finishes before the user stops).
 */
const pauseFinalizations = new Map<string, PauseFinalizationEntry>();

/**
 * Open settle callbacks. Survive `consume` so a superseding stop can still fail
 * a hand-off that is already awaited but whose Rust IPC has not finished.
 */
const openResolves = new Map<
  string,
  (outcome: RustChunkUploadPauseFinalizationOutcome) => void
>();

/**
 * Starts the Rust pause-finalization hand-off before the blocking stop IPC runs.
 * A subsequent stop-from-pause consumes this exact promise, rather than reading
 * an eventually-consistent session failure marker.
 */
export function beginRustChunkUploadPauseFinalization(
  sessionId: string
): PauseFinalizationEntry {
  // A second pause for the same session supersedes the first hand-off. Wake any
  // consumer of the old promise into recovery rather than leaving it pending.
  clearRustChunkUploadPauseFinalization(sessionId);

  let settle!: (outcome: RustChunkUploadPauseFinalizationOutcome) => void;
  const promise = new Promise<RustChunkUploadPauseFinalizationOutcome>(
    (resolvePromise) => {
      let settled = false;
      settle = (outcome) => {
        if (settled) return;
        settled = true;
        openResolves.delete(sessionId);
        resolvePromise(outcome);
      };
    }
  );
  const entry = { promise, resolve: settle };
  pauseFinalizations.set(sessionId, entry);
  openResolves.set(sessionId, settle);
  return entry;
}

/**
 * Returns the one-time pause finalization for a session. Missing state fails
 * closed: callers must route to upload recovery instead of generating notes.
 *
 * Removes the hand-off so End-session cannot double-consume it. The returned
 * promise may already be settled when pause finished before the click.
 */
export function consumeRustChunkUploadPauseFinalization(
  sessionId: string
): Promise<RustChunkUploadPauseFinalizationOutcome> | undefined {
  const entry = pauseFinalizations.get(sessionId);
  if (!entry) return undefined;

  pauseFinalizations.delete(sessionId);
  return entry.promise;
}

/**
 * Whether a pause hand-off for this session is STILL IN FLIGHT — begun and not
 * yet settled.
 *
 * Reads {@link openResolves}, not {@link pauseFinalizations}: the two maps
 * answer different questions and only this one answers ours. `pauseFinalizations`
 * is deleted by `consume` and deliberately outlives settlement, so it says
 * "has a hand-off been collected", not "is the pause IPC still running".
 * `openResolves` is set at `begin` and deleted the instant the promise settles
 * (or is superseded), so `has()` is exactly "a stop for this session is in
 * flight right now".
 *
 * FR-038 needs this because `online_chunk_upload_get_active` reports `active`
 * while the native session is Stopping as well as while it is Active, so the
 * sweep on its own cannot tell a genuine runaway from a stop that is already
 * draining. The client already knows which it is; this is how it asks.
 */
export function hasOpenRustChunkUploadPauseFinalization(sessionId: string): boolean {
  return openResolves.has(sessionId);
}

/**
 * Fails a hand-off closed when another stop supersedes it, including one that
 * has already been consumed and is awaiting the Rust IPC result.
 */
export function clearRustChunkUploadPauseFinalization(sessionId: string): void {
  const settle = openResolves.get(sessionId);
  settle?.('unhealthy');
  openResolves.delete(sessionId);
  pauseFinalizations.delete(sessionId);
}

/** Legacy (flag-OFF) failed-chunk tolerance — unchanged today's behaviour. */
export const LEGACY_FAILED_CHUNK_THRESHOLD = 3;

/**
 * Resolves the failed-chunk tolerance shared by the legacy (non-Rust-upload)
 * `chunksIssues > N` gate in `useMutateStopRecording.ts` and
 * `useMutateStopSessionFromPause.ts` — flag OFF keeps today's hardcoded `3`;
 * flag ON aligns on the same payload-configurable threshold as the Rust path
 * (FR-022a).
 */
export function resolveChunkFailureThreshold(
  selfHealingEnabled: boolean,
  failedChunkThreshold: number
): number {
  return selfHealingEnabled ? failedChunkThreshold : LEGACY_FAILED_CHUNK_THRESHOLD;
}

/**
 * Decides whether the FE asks Rust to detach the backup mux+upload
 * from the blocking stop IPC, so note generation is dispatched as soon as the
 * chunk stream is drained and `:finish` has settled.
 *
 * Two independent reasons to ask:
 *
 *  - **Kill switch ON** (`release-desktop-detached-backup-mux`): the
 *    rollout switch for every recording, v1 included.
 *  - **Transcription-service V2 recording, on a FINAL stop only**: the
 *    transcript the backend generates from is the streamed chunk transcript,
 *    which is complete and healthy at stop; the MP3 backup is redundancy only.
 *    Waiting for the mux (fleet p95 ~51s, minutes for long sessions) plus the
 *    upload before kicking note generation is the ~4-5 minute "Processing
 *    transcript" dead time on a V2 stop, so a V2 recording requests the detach
 *    regardless of the kill switch. v1 recordings still follow the kill switch,
 *    because v1 needs the backup and `:finish` before the transcript is
 *    finalised.
 *
 * Why the V2 rule excludes a pause (`isPaused`), while the kill switch does
 * not: Rust admits at most `MAX_DETACHED_EXTRACTIONS =
 * FFMPEG_MAX_CONCURRENCY - FFMPEG_LIVE_CHUNK_RESERVE` detached jobs
 * (`src-tauri/src/session/backup_dispatch.rs`), and that difference is
 * **exactly one** (2 - 1). A pause never dispatches `_actionPostRecording`, so
 * detaching there buys only a shorter pause IPC — while holding the single
 * slot for the whole WAV→MP3 mux. A clinician who pauses, resumes and ends
 * inside that window hits a declined admission (`bound_full`) and takes an
 * INLINE mux on the stop that *does* gate note generation: the exact DSK-898
 * stall this change exists to remove, on a very common gesture. A pause
 * therefore keeps today's kill-switch-only request, leaving the slot free for
 * End-session.
 *
 * This only *requests* the detach. Rust still owns the decision
 * (`admit_detached_mux`): it declines and muxes inline when the chunk stream
 * is past the failed-chunk threshold, when the WAV snapshot cannot be taken,
 * when the single detached slot is busy, or when the durable recovery row
 * cannot be written. The healthy gate below reads the disposition Rust
 * reports (`backupPendingAsync` / `backupUploaded`), never this request.
 */
export function shouldDetachBackupFromStop({
  detachedBackupMuxEnabled,
  usesTranscriptionServiceV2,
  isPaused,
}: {
  /** `release-desktop-detached-backup-mux`, read per stop. */
  detachedBackupMuxEnabled: boolean;
  /** The recording's V2-ness as locked at start (`tauriRecordModel.useTranscriptionServiceV2`). */
  usesTranscriptionServiceV2: boolean;
  /**
   * Whether this stop is a pause segment (`reason: 'pause'`) rather than the
   * note-generating final stop. The V2 rule applies only to the latter.
   */
  isPaused: boolean;
}): boolean {
  return detachedBackupMuxEnabled || (usesTranscriptionServiceV2 && !isPaused);
}

/**
 * Mirrors the Rust stop safety gate used by the main stop flow.
 *
 * Flag OFF (`selfHealingEnabled: false`, the default): unchanged legacy
 * behaviour — note generation requires a confirmed backup upload *and*
 * `finishSucceeded`, tolerating up to {@link LEGACY_FAILED_CHUNK_THRESHOLD}
 * failed chunks.
 *
 * Flag ON (FR-022/FR-022a/FR-034): `backupUploaded` is dropped from the
 * healthy gate — the backup is a non-blocking parallel stream, not a
 * precondition for generating from the chunk transcript — and the tolerated
 * failed-chunk count comes from the flag's Flagsmith payload
 * (`failedChunkThreshold`, default 0 = any genuine failed chunk is unhealthy).
 *
 * On the legacy branch a `backupPendingAsync` stop counts as backup-satisfied.
 * `pendingAsync` covers two different Rust paths, and only one of them is
 * literally "as safe as uploaded":
 *
 *  - **Detached upload** (`asyncHealthyBackup`): the MP3 is retained encrypted
 *    with a durable recovery row *before* the stop returns; only the upload is
 *    detached. Genuinely as safe as uploaded.
 *  - **Detached mux** (`detachedBackupMux`): the mux itself runs after the stop,
 *    so there is no MP3 to retain at this point — but a durable row still exists
 *    before the stop returns. Rust writes a `detached_mux_recovery` row naming
 *    the snapshotted source WAVs as a *condition* of granting the detach
 *    (`admit_detached_mux`); if it cannot be written the mux runs inline
 *    instead. So `pendingAsync` here means "durably recorded, not yet muxed"
 *    rather than "durably retained", and the audio survives a failed mux, a
 *    crash, and the next launch's storage sweep — the row holds `remove_directory`
 *    off its sources and the boot recovery pass re-muxes and retains them.
 *    Rust additionally refuses the detach past the failed-chunk threshold, so
 *    this is only ever a redundant backup, never the session's primary copy.
 *    What it still does NOT give us is a *prompt* FE-visible signal: a detached
 *    mux that fails is recovered on the next launch, not surfaced on this stop.
 *
 * Reading the reported disposition rather than the detached-mux flag matters
 * in either case, because the flag only
 * *requests* a detach — when the prepare phase cannot move the shared source
 * WAVs somewhere private it declines and extracts inline, and a failed inline
 * extraction reports neither uploaded nor pending. Gating on the flag would
 * call that lost backup healthy. Nothing else on this branch moves:
 * `finishSucceeded` is still required and the tolerance stays at
 * {@link LEGACY_FAILED_CHUNK_THRESHOLD}, because self-healing's stricter
 * payload threshold is a separate rollout. When both flags are on,
 * self-healing's branch wins: it is strictly stricter on chunks.
 */
export function getRustChunkUploadPauseFinalizationOutcome({
  stopResult,
  stopThrew,
  selfHealingEnabled = false,
  failedChunkThreshold = LEGACY_FAILED_CHUNK_THRESHOLD,
}: {
  stopResult: StopOnlineChunkUploadResult | undefined;
  stopThrew: boolean;
  /** FR-034: drop `backupUploaded` from the healthy gate when true. */
  selfHealingEnabled?: boolean;
  /** FR-022a: payload-configurable tolerated failed-chunk count (flag ON only). */
  failedChunkThreshold?: number;
}): RustChunkUploadPauseFinalizationOutcome {
  const threshold = resolveChunkFailureThreshold(
    selfHealingEnabled,
    failedChunkThreshold
  );
  const chunkUploadsFailed =
    !!stopResult &&
    (stopResult.failedChunks > threshold ||
      (stopResult.totalChunks === 0 && stopResult.failedChunks > 0));

  if (selfHealingEnabled) {
    // FR-034: healthy no longer requires a confirmed backup upload.
    const recordingSafe =
      !stopThrew && stopResult?.finishSucceeded === true && !chunkUploadsFailed;
    return recordingSafe ? 'healthy' : 'unhealthy';
  }

  // `pendingAsync` counts here for both Rust paths it covers — see the note
  // above for why the detached-mux one is safe despite not being retained yet.
  // Read the reported disposition, never the flag that merely asked for the
  // detach: a declined snapshot falls back to an inline extraction.
  const backupConfirmed =
    !stopThrew &&
    (stopResult?.backupPendingAsync === true || stopResult?.backupUploaded === true) &&
    stopResult?.finishSucceeded === true;
  return backupConfirmed && !chunkUploadsFailed ? 'healthy' : 'unhealthy';
}
